import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import { createCheckout, formatPrice, formatApiError } from "@/lib/api";
import { FALLBACK_IMG } from "@/lib/images";
import { toast } from "sonner";
import { CreditCard, Gift, Landmark, Loader2, Lock, Minus, Plus, Trash2, Truck } from "lucide-react";

const FREE_SHIPPING = 750;

const METHODS = [
  { id: "kapida", title: "Kapıda Ödeme", desc: "Sipariş teslimatında ödeme", icon: Truck },
  { id: "havale", title: "Havale / EFT", desc: "Banka bilgileri sipariş sonrası paylaşılır", icon: Landmark },
  { id: "kart", title: "Kredi / Banka Kartı", desc: "iyzico güvenli ödeme sayfasına yönlendirilirsin", icon: CreditCard },
];

const FIELDS = [
  { name: "name", label: "Ad", autoComplete: "given-name" },
  { name: "surname", label: "Soyad", autoComplete: "family-name" },
  { name: "email", label: "E-posta", type: "email", autoComplete: "email" },
  { name: "phone", label: "Telefon", type: "tel", autoComplete: "tel", placeholder: "05xx xxx xx xx" },
  { name: "city", label: "Şehir", autoComplete: "address-level2" },
];

export default function Cart() {
  const { cart, updateQty, removeFromCart, subtotal } = useStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [checkingOut, setCheckingOut] = useState(false);
  const [form, setForm] = useState({
    name: "", surname: "", email: "", phone: "", address: "", city: "", payment_method: "kapida",
  });
  const shipping = subtotal >= FREE_SHIPPING || subtotal === 0 ? 0 : 79;
  const total = subtotal + shipping;
  const freeShippingRemaining = Math.max(0, FREE_SHIPPING - subtotal);
  const freeShippingProgress = Math.min(100, (subtotal / FREE_SHIPPING) * 100);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        email: f.email || user.email || "",
        name: f.name || (user.name || "").split(" ").slice(0, -1).join(" ") || "",
        surname: f.surname || (user.name || "").split(" ").slice(-1)[0] || "",
      }));
    }
  }, [user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const checkout = async (e) => {
    e.preventDefault();
    setCheckingOut(true);
    try {
      const items = cart.map((i) => ({ slug: i.slug, quantity: i.qty, variant: i.variant, giftWrap: i.giftWrap }));
      const r = await createCheckout({ items, origin_url: window.location.origin, ...form });
      if (r.payment_page_url) {
        window.location.href = r.payment_page_url;
        return;
      }
      navigate(`/odeme/basarili?order_id=${r.order_id}&method=${r.payment_method}`);
    } catch (err) {
      toast.error(formatApiError(err));
      setCheckingOut(false);
    }
  };

  return (
    <>
      <Seo title="Sepet" path="/sepet"  noindex />
      <div className="container-shule py-10 lg:py-16">
        <h1 data-testid="cart-page-title" className="font-serif text-4xl lg:text-5xl text-shule-coffee">Sepetin</h1>

        {cart.length === 0 ? (
          <div data-testid="cart-page-empty" className="py-20 text-center">
            <p className="font-serif text-2xl text-shule-mocha">Sepetin henüz boş</p>
            <p className="text-sm text-shule-soft mt-2">Anına eşlik edecek parçalar seni bekliyor.</p>
            <Link to="/kategori/kolyeler" data-testid="cart-page-browse-button" className="btn-primary-shule mt-6">Keşfetmeye Başla</Link>
          </div>
        ) : (
          <form onSubmit={checkout} className="mt-10 grid lg:grid-cols-[1fr_420px] gap-10 items-start">
            <ul className="space-y-4">
              {cart.map((item) => (
                <li key={item.key} data-testid={`cart-page-item-${item.slug}`}
                  className="flex gap-4 rounded-3xl border border-shule-line bg-shule-paper p-4">
                  <img src={item.image || FALLBACK_IMG} alt={item.name} onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                    className="w-24 h-28 object-cover rounded-2xl border border-shule-line" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <Link to={`/urun/${item.slug}`} className="font-serif text-xl leading-tight hover:text-shule-goldhover transition-colors">{item.name}</Link>
                        <p className="text-xs text-shule-soft mt-1">{item.variant}</p>
                        {item.giftWrap && (
                          <p className="text-xs text-shule-goldhover mt-1 inline-flex items-center gap-1">
                            <Gift className="w-3 h-3" /> Hediye paketli
                          </p>
                        )}
                      </div>
                      <button type="button" data-testid={`cart-page-remove-${item.slug}`} onClick={() => removeFromCart(item.key)}
                        className="w-11 h-11 flex items-center justify-center text-shule-soft hover:text-shule-coffee" aria-label="Kaldır">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-shule-lineaccent rounded-full">
                        <button type="button" data-testid={`cart-page-minus-${item.slug}`} onClick={() => updateQty(item.key, -1)} className="w-10 h-10 flex items-center justify-center" aria-label="Azalt">
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-7 text-center text-sm font-bold">{item.qty}</span>
                        <button type="button" data-testid={`cart-page-plus-${item.slug}`} onClick={() => updateQty(item.key, 1)} className="w-10 h-10 flex items-center justify-center" aria-label="Artır">
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <span className="font-serif text-xl">{formatPrice(item.price * item.qty)}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <aside data-testid="cart-summary" className="rounded-[2rem] border border-shule-line bg-shule-paper p-6 lg:sticky lg:top-28">
              <h2 className="font-serif text-2xl mb-5">Sipariş Özeti</h2>
              <div className="mb-5 rounded-2xl border border-shule-line bg-shule-base p-4">
                <div className="flex items-center justify-between gap-3 text-xs">
                  <span className="font-semibold text-shule-coffee">
                    {freeShippingRemaining > 0 ? `Ücretsiz kargoya ${formatPrice(freeShippingRemaining)} kaldı` : "Ücretsiz kargo kazandın"}
                  </span>
                  <span className="text-shule-soft">{Math.round(freeShippingProgress)}%</span>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-shule-line">
                  <div className="h-full bg-shule-gold transition-all" style={{ width: `${freeShippingProgress}%` }} />
                </div>
              </div>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between"><dt className="text-shule-mocha">Ara toplam</dt><dd data-testid="summary-subtotal" className="font-semibold">{formatPrice(subtotal)}</dd></div>
                <div className="flex justify-between"><dt className="text-shule-mocha">Kargo</dt><dd data-testid="summary-shipping" className="font-semibold">{shipping === 0 ? "Ücretsiz" : formatPrice(shipping)}</dd></div>
                <div className="flex justify-between pt-3 border-t border-shule-line"><dt className="font-semibold">Toplam</dt><dd data-testid="summary-total" className="font-serif text-2xl">{formatPrice(total)}</dd></div>
              </dl>

              <h3 className="font-serif text-xl mt-7 mb-3">Teslimat Bilgileri</h3>
              <div className="grid grid-cols-2 gap-3">
                {FIELDS.map((f) => (
                  <label key={f.name} className={f.name === "email" || f.name === "address" ? "col-span-2" : ""}>
                    <span className="sr-only">{f.label}</span>
                    <input data-testid={`checkout-${f.name}-input`} required value={form[f.name]} onChange={set(f.name)}
                      type={f.type || "text"} autoComplete={f.autoComplete} placeholder={f.placeholder || f.label}
                      className="w-full min-h-[44px] rounded-full border border-shule-lineaccent bg-shule-base px-4 text-sm outline-none focus:border-shule-gold placeholder:text-shule-soft" />
                  </label>
                ))}
                <label className="col-span-2">
                  <span className="sr-only">Adres</span>
                  <textarea data-testid="checkout-address-input" required minLength={10} rows={2} value={form.address} onChange={set("address")}
                    autoComplete="street-address" placeholder="Adres (mahalle, sokak, bina, daire)"
                    className="w-full rounded-2xl border border-shule-lineaccent bg-shule-base px-4 py-3 text-sm outline-none focus:border-shule-gold placeholder:text-shule-soft resize-none" />
                </label>
              </div>

              <h3 className="font-serif text-xl mt-7 mb-3">Ödeme Yöntemi</h3>
              <div className="space-y-2" role="radiogroup" aria-label="Ödeme yöntemi">
                {METHODS.map((m) => (
                  <label key={m.id} data-testid={`payment-method-${m.id}`}
                    className={`flex items-center gap-3 rounded-2xl border p-3.5 cursor-pointer transition-colors ${
                      form.payment_method === m.id ? "border-shule-gold bg-shule-goldlight/30" : "border-shule-lineaccent hover:border-shule-gold/60"}`}>
                    <input type="radio" name="payment_method" value={m.id} checked={form.payment_method === m.id}
                      onChange={set("payment_method")} className="sr-only" />
                    <m.icon className={`w-5 h-5 shrink-0 ${form.payment_method === m.id ? "text-shule-goldhover" : "text-shule-soft"}`} />
                    <span>
                      <span className="block text-sm font-bold">{m.title}</span>
                      <span className="block text-xs text-shule-soft mt-0.5">{m.desc}</span>
                    </span>
                  </label>
                ))}
              </div>

              <button data-testid="checkout-button" type="submit" disabled={checkingOut}
                className="btn-primary-shule w-full mt-6 disabled:opacity-50">
                {checkingOut ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                {checkingOut ? "İşleniyor…" : form.payment_method === "kart" ? `${formatPrice(total)} · Güvenli Ödemeye Geç` : `${formatPrice(total)} · Siparişi Tamamla`}
              </button>
              <p className="mt-3 text-[11px] text-shule-soft text-center leading-relaxed">
                Sipariş tutarı sunucudaki güncel ürün fiyatlarıyla ödeme öncesinde yeniden doğrulanır. Kart ödemesi iyzico güvenli sayfasında tamamlanır.
              </p>
            </aside>
          </form>
        )}
      </div>
    </>
  );
}
